import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Calendar, 
  RefreshCw, 
  Download, 
  BookOpen,
  Bookmark,
  Moon,
  Sun,
  ChevronRight,
  Heart,
  Target,
  Compass,
  Info,
  ExternalLink,
  FileText,
  Bell,
  BellOff,
  Users,
  Mail
} from 'lucide-react';
import ContentCard from '../components/common/ContentCard';

const WELCOME_DISABLED_KEY = 'dmu-rimp-welcome-disabled';

const ProfilePage = ({ 
  currentDay,
  retreatStartDate,
  dailyNotes,
  bookmarkedDays,
  darkMode,
  onToggleDarkMode,
  onOpenSync,
  onOpenRetreatStart,
  onOpenProfile,
  onOpenReminder,
  onOpenWeeklySummary,
  onOpenPartner
}) => {
  const navigate = useNavigate();
  const [welcomeEnabled, setWelcomeEnabled] = useState(true);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  
  // Load settings
  useEffect(() => {
    const isWelcomeDisabled = localStorage.getItem(WELCOME_DISABLED_KEY) === 'true';
    setWelcomeEnabled(!isWelcomeDisabled);
    
    const isReminderEnabled = localStorage.getItem('dmu-rimp-reminder-enabled') === 'true';
    setReminderEnabled(isReminderEnabled);
  }, []);

  // Toggle welcome screen
  const toggleWelcome = () => {
    const newValue = !welcomeEnabled;
    setWelcomeEnabled(newValue);
    if (newValue) {
      localStorage.removeItem(WELCOME_DISABLED_KEY);
    } else {
      localStorage.setItem(WELCOME_DISABLED_KEY, 'true');
    }
  };
  
  const completedDays = Object.keys(dailyNotes).filter(k => dailyNotes[k]?.trim()).length;
  const bookmarkedCount = Object.keys(bookmarkedDays).filter(k => bookmarkedDays[k]).length;

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get current stage
  const getCurrentStage = () => {
    if (!currentDay) return { name: 'Not Started', color: 'gray' };
    if (currentDay <= 7) return { name: 'Relationship', color: 'rose' };
    if (currentDay <= 18) return { name: 'Identity', color: 'amber' };
    if (currentDay <= 25) return { name: 'Mission', color: 'teal' };
    return { name: 'Purpose', color: 'violet' };
  };

  const stage = getCurrentStage();

  // Handle PDF downloads
  const handleFullPDF = () => {
    window.open('https://www.regnumchristi.com/en/retreat-guides/', '_blank');
  };

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Header with Avatar */}
      <div className="text-center mb-6">
        <div className="relative inline-block">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] flex items-center justify-center mx-auto mb-4 shadow-lg">
            <User className="w-12 h-12 text-white" />
          </div>
          {currentDay && (
            <div className={`absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-${stage.color}-500 flex items-center justify-center text-white font-bold text-sm shadow-md border-4 border-white`}>
              {currentDay}
            </div>
          )}
        </div>
        <h1 className="font-heading text-2xl text-[var(--primary)] font-bold mb-1">
          My Profile
        </h1>
        <p className="text-gray-500 text-sm">Manage your retreat journey</p>
      </div>

      {/* Journey Progress Card */}
      <div className="bg-gradient-to-br from-[var(--primary)] via-[var(--primary)] to-[var(--primary-dark)] rounded-2xl p-5 text-white shadow-lg">
        <h3 className="font-medium text-white/80 mb-4 text-sm uppercase tracking-wider">Your Journey</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
              <Calendar className="w-6 h-6" />
            </div>
            <p className="text-2xl font-bold">{currentDay || 0}</p>
            <p className="text-white/60 text-xs">Current Day</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
              <BookOpen className="w-6 h-6" />
            </div>
            <p className="text-2xl font-bold">{completedDays}</p>
            <p className="text-white/60 text-xs">Reflections</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
              <Bookmark className="w-6 h-6" />
            </div>
            <p className="text-2xl font-bold">{bookmarkedCount}</p>
            <p className="text-white/60 text-xs">Bookmarked</p>
          </div>
        </div>
        {/* Current Stage Indicator */}
        <div className="mt-4 pt-4 border-t border-white/20">
          <p className="text-white/70 text-xs mb-2">Current Stage</p>
          <div className="flex items-center gap-2">
            {stage.name === 'Relationship' && <Heart className="w-5 h-5" />}
            {stage.name === 'Identity' && <User className="w-5 h-5" />}
            {stage.name === 'Mission' && <Target className="w-5 h-5" />}
            {stage.name === 'Purpose' && <Compass className="w-5 h-5" />}
            <span className="font-semibold">{stage.name}</span>
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">Settings</p>
        
        <div 
          onClick={onOpenRetreatStart}
          className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-50 hover:border-[var(--primary)]/20 transition-all shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Retreat Start Date</h4>
              <p className="text-sm text-gray-500">{formatDate(retreatStartDate)}</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>

        <div 
          onClick={onToggleDarkMode}
          className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-50 hover:border-[var(--primary)]/20 transition-all shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center">
              {darkMode ? (
                <Moon className="w-6 h-6 text-indigo-600" />
              ) : (
                <Sun className="w-6 h-6 text-indigo-600" />
              )}
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Appearance</h4>
              <p className="text-sm text-gray-500">{darkMode ? 'Dark Mode' : 'Light Mode'}</p>
            </div>
          </div>
          <div className={`w-14 h-8 rounded-full transition-colors ${darkMode ? 'bg-[var(--primary)]' : 'bg-gray-300'} flex items-center ${darkMode ? 'justify-end' : 'justify-start'} px-1`}>
            <div className="w-6 h-6 rounded-full bg-white shadow-md" />
          </div>
        </div>

        <div 
          onClick={toggleWelcome}
          className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-50 hover:border-[var(--primary)]/20 transition-all shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center">
              {welcomeEnabled ? (
                <Bell className="w-6 h-6 text-teal-600" />
              ) : (
                <BellOff className="w-6 h-6 text-teal-600" />
              )}
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Daily Welcome</h4>
              <p className="text-sm text-gray-500">{welcomeEnabled ? 'Enabled' : 'Disabled'}</p>
            </div>
          </div>
          <div className={`w-14 h-8 rounded-full transition-colors ${welcomeEnabled ? 'bg-[var(--primary)]' : 'bg-gray-300'} flex items-center ${welcomeEnabled ? 'justify-end' : 'justify-start'} px-1`}>
            <div className="w-6 h-6 rounded-full bg-white shadow-md" />
          </div>
        </div>

        <div 
          onClick={onOpenReminder}
          className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-50 hover:border-[var(--primary)]/20 transition-all shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center">
              <Bell className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Prayer Reminder</h4>
              <p className="text-sm text-gray-500">{reminderEnabled ? 'Enabled' : 'Set daily reminder'}</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>

      {/* Data & Sync */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">Data & Sync</p>
        
        <div 
          onClick={onOpenSync}
          className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200 cursor-pointer hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900">Cloud Sync</h4>
              <p className="text-sm text-blue-600">Backup progress across devices</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-blue-400" />
        </div>

        <div 
          onClick={onOpenProfile}
          className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 cursor-pointer hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-purple-900">Self-Discovery Profile</h4>
              <p className="text-sm text-purple-600">Spiritual insights & discoveries</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-purple-400" />
        </div>
      </div>

      {/* Social & Sharing */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">Social & Sharing</p>
        
        <div 
          onClick={onOpenWeeklySummary}
          className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 cursor-pointer hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-amber-900">Weekly Summary</h4>
              <p className="text-sm text-amber-600">View & share your week's progress</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-400" />
        </div>

        <div 
          onClick={onOpenPartner}
          className="flex items-center justify-between p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border border-rose-200 cursor-pointer hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-rose-900">Accountability Partner</h4>
              <p className="text-sm text-rose-600">Invite a friend to join you</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-rose-400" />
        </div>
      </div>

      {/* PDF Downloads */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">Downloads</p>
        
        <div 
          onClick={handleFullPDF}
          className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200 cursor-pointer hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-emerald-900">Retreat Guides (PDF)</h4>
              <p className="text-sm text-emerald-600">Download for offline use</p>
            </div>
          </div>
          <ExternalLink className="w-5 h-5 text-emerald-400" />
        </div>
      </div>

      {/* Quick Access */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">Quick Access</p>
        
        <ContentCard
          icon={BookOpen}
          title="My Journal"
          subtitle={`${completedDays} entries written`}
          onClick={() => navigate('/journal')}
        />

        <ContentCard
          icon={Bookmark}
          title="Bookmarked Days"
          subtitle={bookmarkedCount > 0 ? `${bookmarkedCount} days saved` : 'No bookmarks yet'}
          onClick={() => navigate('/journal')}
        />

        <ContentCard
          icon={Info}
          title="About This Retreat"
          subtitle="Learn more, share QR code"
          onClick={() => navigate('/about')}
        />
      </div>

      {/* Version & Credits */}
      <div className="text-center pt-4 border-t border-gray-100 space-y-2">
        <p className="text-xs text-gray-500 font-medium">
          DMU Lenten Retreat v2.0
        </p>
        <p className="text-xs text-gray-400">
          © {new Date().getFullYear()} Divine Mercy University
        </p>
        <p className="text-xs text-gray-400">
          Legion of Christ & Regnum Christi Movement
        </p>
      </div>
    </div>
  );
};

export default ProfilePage;
